import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function UserTicketCountReport() {
  const { t } = useTranslation();

  const [vendors, setVendors] = useState([]);
  const [buses, setBuses] = useState([]);

  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedBusId, setSelectedBusId] = useState("");

  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [report, setReport] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });

  // =========================================================
  // FETCH VENDORS
  // =========================================================

  const fetchVendors = async () => {
    setLoadingVendors(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/vendors`);
      const vendorList = Array.isArray(res.data) ? res.data : [];

      setVendors(vendorList);
      setSelectedVendorId(vendorList.length === 1 ? String(vendorList[0].vendor_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible vendors", err);
      setVendors([]);
      setSelectedVendorId("");
    } finally {
      setLoadingVendors(false);
    }
  };

  // =========================================================
  // FETCH BUSES
  // =========================================================

  const fetchBuses = async (vendorId) => {
    if (!vendorId) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    setLoadingBuses(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/buses`, {
        params: { vendor_id: vendorId },
      });

      const busList = Array.isArray(res.data) ? res.data : [];

      setBuses(busList);
      setSelectedBusId(busList.length === 1 ? String(busList[0].bus_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible buses", err);
      setBuses([]);
      setSelectedBusId("");
    } finally {
      setLoadingBuses(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchVendors();
  }, []);

  // =========================================================
  // VENDOR CHANGE
  // =========================================================

  useEffect(() => {
    setReport([]);
    setMessage("");
    setMessageVariant("info");

    if (selectedVendorId) {
      fetchBuses(selectedVendorId);
    } else {
      setBuses([]);
      setSelectedBusId("");
    }
  }, [selectedVendorId]);

  // =========================================================
  // BUS CHANGE
  // =========================================================

  useEffect(() => {
    setReport([]);
    setMessage("");
    setMessageVariant("info");
  }, [selectedBusId]);

  const handleVendorChange = (e) => {
    setSelectedVendorId(e.target.value);
    setSelectedBusId("");
  };

  const handleBusChange = (e) => {
    setSelectedBusId(e.target.value);
  };

  const vendorDropdownDisabled = loadingVendors || vendors.length <= 1;
  const busDropdownDisabled = loadingBuses || !selectedVendorId || buses.length <= 1;

  // =========================================================
  // FETCH REPORT
  // =========================================================

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end || !selectedVendorId || !selectedBusId) return;

    setLoadingReport(true);
    setMessage("");
    setMessageVariant("info");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/driver/ticket-meter-in-time`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
            vendor_id: Number(selectedVendorId),
            bus_id: Number(selectedBusId),
          },
        }
      );

      const reportData = Array.isArray(res.data) ? res.data : [];

      setReport(reportData);

      if (reportData.length === 0) {
        setMessage(t("user_ticket_count_report.messages.empty"));
        setMessageVariant("info");
      } else {
        setMessage("");
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch ticket count report", err);
      setReport([]);
      setMessage(t("user_ticket_count_report.messages.fetch_failed"));
      setMessageVariant("error");
    } finally {
      setLoadingReport(false);
    }
  };

  // =========================================================
  // STATE
  // =========================================================

  const canGetReport = Boolean(
    timeRange.start &&
    timeRange.end &&
    selectedVendorId &&
    selectedBusId &&
    !loadingReport
  );

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <StandardScreen
      topNav={<TopNav title={t("user_ticket_count_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* Filters */}

            <Card title={t("user_ticket_count_report.title")}>
              <Stack gap="3">

                <FormField label={t("user_ticket_count_report.fields.vendor.label")}>
                  <SelectInput
                    value={selectedVendorId}
                    disabled={vendorDropdownDisabled}
                    onChange={handleVendorChange}
                  >
                    <option value="">
                      {loadingVendors
                        ? t("user_ticket_count_report.fields.vendor.loading")
                        : t("user_ticket_count_report.fields.vendor.placeholder")}
                    </option>

                    {vendors.map((vendor) => (
                      <option key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.vendor_id} | {vendor.vendor_name}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_ticket_count_report.fields.bus.label")}>
                  <SelectInput
                    value={selectedBusId}
                    disabled={busDropdownDisabled}
                    onChange={handleBusChange}
                  >
                    <option value="">
                      {loadingBuses
                        ? t("user_ticket_count_report.fields.bus.loading")
                        : t("user_ticket_count_report.fields.bus.placeholder")}
                    </option>

                    {buses.map((bus) => (
                      <option key={bus.bus_id} value={bus.bus_id}>
                        {bus.bus_id} | {bus.plate_number}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField>
                  <div>
                    <TimeRangeSelector onRangeChange={setTimeRange} />
                  </div>
                </FormField>

                <Button onClick={fetchReport} disabled={!canGetReport} fullWidth>
                  {loadingReport
                    ? t("user_ticket_count_report.actions.loading")
                    : t("user_ticket_count_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>

            {/* Table */}

            {report.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky>
                          {t("user_ticket_count_report.table.wallet")}
                        </Th>

                        <Th columnIndex={1} sticky align="right">
                          {t("user_ticket_count_report.table.net_tickets")}
                        </Th>
                      </tr>
                    </thead>

                    <tbody>
                      {report.map((item, index) => (
                        <tr key={`${item.route}-${index}`}>
                          <Td columnIndex={0} sticky>
                            {item.route}
                          </Td>

                          <NumberTd
                            columnIndex={1}
                            sticky
                            style={{
                              fontSize: "2.8rem",
                              fontWeight: 800,
                              color:
                                item.net_tickets > 0
                                  ? "#16a34a"
                                  : item.net_tickets < 0
                                  ? "#dc2626"
                                  : undefined,
                            }}
                          >
                            {item.net_tickets}
                          </NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}